/**
 * Shared building blocks for the Admin User Editor (AR-327).
 *
 * The editor is a design/layout deliverable: fields read from the member
 * record API, and every write — save, credential actions, Stripe cancel,
 * HubSpot delete, Showcase unpublish — is a trigger point whose execution
 * lands in a companion backend ticket. `DeferredAction` is how that contract
 * is made visible: a real confirmation flow that stops short of the call.
 */
import React, { ReactNode, useId, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Info, Lock } from 'lucide-react';
import { EM_DASH } from '@/lib/adminUserRecordApi';

/** A titled white card — the editor is a stack of these in every tab. */
export function RecordCard({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800',
        className,
      )}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Responsive field grid — one column on phones, `cols` on desktop. */
export function FieldGrid({ cols = 3, children }: { cols?: 2 | 3; children: ReactNode }) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 sm:grid-cols-2',
        cols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2',
      )}
    >
      {children}
    </div>
  );
}

const labelClass = 'text-xs font-medium text-slate-500 dark:text-slate-400';

/** Editable text field bound to a record value. */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  type?: string;
}) {
  // Labels repeat across the page (billing and mailing both have City/ZIP),
  // so the label alone cannot key `htmlFor`.
  const uid = useId();
  const id = `field-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${uid}`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className={labelClass}>
        {label}
        {readOnly && <span className="ml-1 text-slate-400">(read-only)</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
        className={cn('h-9 text-sm', readOnly && 'bg-slate-50 text-slate-600 dark:bg-gray-900')}
      />
    </div>
  );
}

/** An option whose stored value differs from what the admin reads. */
export interface SelectOption {
  value: string;
  label: string;
}

const toOption = (option: string | SelectOption): SelectOption =>
  typeof option === 'string' ? { value: option, label: option } : option;

/**
 * Select bound to a record value.
 *
 * AR-382: a stored value outside `options` (a PRO timezone, a legacy
 * salutation) is carried at the head of the list. Radix renders an unmatched
 * value as blank, which would make the field disagree with the row and read
 * as "not set".
 */
export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder = 'Not set',
  readOnly,
}: {
  label: string;
  value: string;
  options: (string | SelectOption)[];
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  const choices = options.map(toOption);
  if (value && !choices.some((option) => option.value === value)) {
    choices.unshift({ value, label: value });
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Label className={labelClass}>
        {label}
        {readOnly && <span className="ml-1 text-slate-400">(read-only)</span>}
      </Label>
      <Select value={value || undefined} onValueChange={onChange} disabled={readOnly}>
        <SelectTrigger
          className={cn('h-9 text-sm', readOnly && 'bg-slate-50 text-slate-600 dark:bg-gray-900')}
          aria-label={label}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {choices.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Static value — used where the record is authoritative and admins cannot edit. */
export function ReadOnlyField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className={labelClass}>
        {label}
        <span className="ml-1 text-slate-400">(read-only)</span>
      </Label>
      <div className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300">
        {value?.trim() ? value : EM_DASH}
      </div>
    </div>
  );
}

const STATE_TONES: Record<string, string> = {
  positive: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  neutral: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-200',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  danger: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  accent: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  dark: 'bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900',
};

export function StatePill({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: keyof typeof STATE_TONES;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold',
        STATE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Placeholder for a section whose feed does not exist yet. Says which system
 * owes the data so the gap reads as scope, not as a bug.
 */
export function PendingIntegration({ source }: { source: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 dark:border-gray-600 dark:bg-gray-900 dark:text-slate-400">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      <p>
        Waiting on {source}. The layout below is final — values fill in when that feed lands.
      </p>
    </div>
  );
}

/** Banner naming what this page does and does not do yet. */
export function DesignScopeNotice({
  bound = false,
  unbound = [],
}: {
  bound?: boolean;
  unbound?: { section: string; field: string; note: string }[];
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-200">
      <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>
        {bound ? (
          <p>
            Bound to the migrated member record. Editable fields save to their own column; read-only
            ones belong to an admin action or an integration (status, plan, role, email, HubSpot,
            Stripe, TRAMS, TRIO) whose workflow ships with its companion ticket.
          </p>
        ) : (
          <p>
            Fields read from the member record API. Saving and the workflows behind each admin
            action (HubSpot sync, Postmark status emails, Stripe cancel, Showcase unpublish,
            credential actions) ship with their companion backend tickets — every trigger point
            below confirms, then stops.
          </p>
        )}
        {bound && unbound.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer font-semibold">
              {unbound.length} fields have no source in the schema yet
            </summary>
            <ul className="mt-1 list-disc pl-5">
              {unbound.map((item) => (
                <li key={`${item.section}-${item.field}`}>
                  <strong>{item.section} · {item.field}</strong> — {item.note}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

export interface DeferredActionField {
  name: string;
  label: string;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
}

/**
 * Confirmation flow for an action whose backend is a separate ticket. Renders
 * the real dialog — title, consequence, inputs, destructive styling, optional
 * type-to-confirm — and reports the deferral instead of calling anything.
 */
export function DeferredAction({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  fields = [],
  confirmPhrase,
  ticketNote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  fields?: DeferredActionField[];
  /** Typed exactly before a destructive confirm unlocks. */
  confirmPhrase?: string;
  ticketNote: string;
}) {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [typed, setTyped] = useState('');

  const reset = () => {
    setValues({});
    setTyped('');
  };

  const requiredMissing = fields.some((field) => !values[field.name]?.trim());
  const phraseMismatch = Boolean(confirmPhrase) && typed.trim() !== confirmPhrase;
  const blocked = requiredMissing || phraseMismatch;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {fields.length > 0 && (
          <div className="flex flex-col gap-3">
            {fields.map((field) =>
              field.multiline ? (
                <div key={field.name} className="flex flex-col gap-1.5">
                  <Label htmlFor={`da-${field.name}`} className={labelClass}>
                    {field.label}
                  </Label>
                  <Textarea
                    id={`da-${field.name}`}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) =>
                      setValues((prev) => ({ ...prev, [field.name]: event.target.value }))
                    }
                  />
                </div>
              ) : (
                <div key={field.name} className="flex flex-col gap-1.5">
                  <Label htmlFor={`da-${field.name}`} className={labelClass}>
                    {field.label}
                  </Label>
                  <Input
                    id={`da-${field.name}`}
                    type={field.type ?? 'text'}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) =>
                      setValues((prev) => ({ ...prev, [field.name]: event.target.value }))
                    }
                  />
                </div>
              ),
            )}
          </div>
        )}

        {confirmPhrase && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="da-confirm-phrase" className={labelClass}>
              Type <span className="font-mono text-slate-700 dark:text-slate-200">{confirmPhrase}</span> to confirm
            </Label>
            <Input
              id="da-confirm-phrase"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={confirmPhrase}
            />
          </div>
        )}

        <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-gray-900 dark:text-slate-400">
          {ticketNote}
        </p>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={blocked}
            className={
              destructive ? 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600' : undefined
            }
            onClick={() => {
              toast({
                title: `${title} — not wired up yet`,
                description: ticketNote,
              });
              reset();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
